import React, { useEffect, useRef, useState } from "react";

// Backend configuration
const BACKEND = (
  process.env.REACT_APP_BACKEND_URL || 
  "https://onetapcast-signal.onrender.com"
).replace(/\/+$/, "");

const WS_BASE = BACKEND.replace(/^http/i, "ws");

// TURN server configuration
function parseTurnUrls(value) {
  if (!value) return [];
  return value.split(",").map(s => s.trim()).filter(Boolean);
}

const TURN_URLS = parseTurnUrls(process.env.REACT_APP_TURN_URLS || "");
const TURN_USERNAME = process.env.REACT_APP_TURN_USERNAME || "";
const TURN_CREDENTIAL = process.env.REACT_APP_TURN_CREDENTIAL || "";
const ICE_POLICY = process.env.REACT_APP_ICE_POLICY || "all";

const ICE_SERVERS = [
  { urls: "stun:stun.l.google.com:19302" },
  ...(TURN_URLS.length ? [{
    urls: TURN_URLS,
    username: TURN_USERNAME,
    credential: TURN_CREDENTIAL
  }] : [])
];

const RTC_CONFIG = {
  iceServers: ICE_SERVERS,
  iceTransportPolicy: ICE_POLICY
};

// Utility functions
function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

async function waitBackendReady(timeoutMs = 30000) {
  const endTime = Date.now() + timeoutMs;
  while (Date.now() < endTime) {
    try {
      const response = await fetch(`${BACKEND}/api/health`, { cache: "no-store" });
      if (response.ok) return true;
    } catch (error) {
      // Ignore errors and retry
    }
    await sleep(1200);
  }
  return false;
}

function newId(length = 10) {
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  return Array.from({ length }, () => chars[Math.floor(Math.random() * chars.length)]).join("");
}

// UI Components
function Button({ children, className = "", ...props }) {
  return (
    <button className={`btn ${className}`} {...props}>
      {children}
    </button>
  );
}

function Badge({ children }) {
  return <span className="badge">{children}</span>;
}

// Icon Components
const IconCast = ({ size = 26 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ marginRight: 6 }}>
    <path 
      d="M2 16a6 6 0 0 1 6 6H6a4 4 0 0 0-4-4v-2Z" 
      fill="#19B394"
    />
    <path 
      d="M2 12a10 10 0 0 1 10 10h-2C10 18.477 7.523 16 4 16H2v-4Z" 
      fill="#19B394" 
      opacity=".6"
    />
    <path 
      d="M21 6H7a1 1 0 0 0-1 1v1H4V7a3 3 0 0 1 3-3h14a3 3 0 0 1 3 3v10a3 3 0 0 1-3 3h-6v-2h6a1 1 0 0 0 1-1V7a1 1 0 0 0-1-1Z" 
      fill="#111827"
    />
  </svg>
);

const IconArrowDownCircle = ({ size = 22, color = "#2F81F7" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="11" fill={color} opacity=".12" />
    <path 
      d="M12 6v9m0 0l4-4m-4 4l-4-4" 
      stroke={color} 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
  </svg>
);

const IconArrowUpCircle = ({ size = 22, color = "#19B394" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="11" fill={color} opacity=".12" />
    <path 
      d="M12 18V9m0 0l4 4m-4-4l-4 4" 
      stroke={color} 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
  </svg>
);

const IconMonitor = ({ size = 54 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <rect 
      x="3" 
      y="4" 
      width="18" 
      height="12" 
      rx="2" 
      stroke="#94a3b8" 
      strokeWidth="1.5"
    />
    <path 
      d="M8 20h8" 
      stroke="#94a3b8" 
      strokeWidth="1.5" 
      strokeLinecap="round"
    />
    <path 
      d="M10 16v4M14 16v4" 
      stroke="#94a3b8" 
      strokeWidth="1.5" 
      strokeLinecap="round"
    />
  </svg>
);

export default function App() {
  // Navigation and UI state
  const [page, setPage] = useState("home");
  const [status, setStatus] = useState("Disconnected");
  const [rxFit, setRxFit] = useState("contain");

  // Fullscreen state tracking
  const fsActiveRef = useRef(false);

  // Receiver state
  const [rxCode, setRxCode] = useState("");
  const [connectedCount, setConnectedCount] = useState(0);
  const [rxMuted, setRxMuted] = useState(true);
  const [rxHasStream, setRxHasStream] = useState(false);
  const rxWsRef = useRef(null);
  const rxPcRef = useRef(null);
  const rxIdRef = useRef(newId());
  const rxVideoRef = useRef(null);
  const rxRunningRef = useRef(false);

  // Sender state
  const [txCode, setTxCode] = useState("");
  const [includeAudio, setIncludeAudio] = useState(true);
  const [senderConnected, setSenderConnected] = useState(false);
  const txWsRef = useRef(null);
  const txPcRef = useRef(null);
  const txIdRef = useRef(newId());
  const txVideoRef = useRef(null);
  const txStreamRef = useRef(null);

  // Utility functions
  async function copyToClipboard(text) {
    try {
      await navigator.clipboard.writeText(text);
    } catch (error) {
      // Ignore clipboard errors
    }
  }

  function enterFullscreenOrImmersive(paneId) {
    const element = document.getElementById(paneId) || document.documentElement;
    if (element.requestFullscreen) {
      element.requestFullscreen()
        .then(() => {
          fsActiveRef.current = true;
        })
        .catch(() => {
          document.body.classList.add("immersive");
          fsActiveRef.current = true;
        });
    } else {
      document.body.classList.add("immersive");
      fsActiveRef.current = true;
    }
  }

  function exitFullscreenNow() {
    if (document.fullscreenElement) {
      try {
        document.exitFullscreen();
      } catch (error) {
        // Ignore exit errors
      }
    }
    
    document.body.classList.remove("immersive");
    document.body.classList.remove("is-fullscreen");
    fsActiveRef.current = false;
    
    setTimeout(() => {
      if (document.fullscreenElement) {
        try {
          document.exitFullscreen();
        } catch (error) {
          // Ignore retry errors
        }
      }
    }, 100);
    
    try {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      // Ignore scroll errors
    }
  }

  // Mobile viewport height fix and CSS variable for fullscreen fit
  useEffect(() => {
    const setViewportHeight = () => {
      document.documentElement.style.setProperty("--vh", `${window.innerHeight * 0.01}px`);
    };

    setViewportHeight();
    window.addEventListener("resize", setViewportHeight);
    return () => window.removeEventListener("resize", setViewportHeight);
  }, []);

  useEffect(() => {
    document.documentElement.style.setProperty("--fs-fit", rxFit === "cover" ? "cover" : "contain");
  }, [rxFit]);

  // Warm backend on app load
  useEffect(() => {
    fetch(`${BACKEND}/api/health`, { cache: "no-store" }).catch(() => {});
  }, []);

  // Fullscreen state management with orientation change support
  useEffect(() => {
    const handleFullscreenChange = () => {
      const isActive = !!document.fullscreenElement;
      document.body.classList.toggle("is-fullscreen", isActive);
      if (!isActive) {
        document.body.classList.remove("immersive");
        fsActiveRef.current = false;
      }
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    window.addEventListener("orientationchange", handleFullscreenChange);
    
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      window.removeEventListener("orientationchange", handleFullscreenChange);
    };
  }, []);

  // Collapse fullscreen when leaving receiver
  useEffect(() => {
    document.body.classList.toggle("receiver-mode", page === "receiver");
    if (page !== "receiver") {
      exitFullscreenNow();
    }
  }, [page]);

  // Auto-start receiver on entering receiver page
  useEffect(() => {
    if (page === "receiver" && !rxRunningRef.current) {
      startReceiver();
    }
  }, [page]);

  // Receiver functions
  async function startReceiver() {
    if (rxRunningRef.current) return;
    rxRunningRef.current = true;

    setStatus("Waking backend...");
    const ready = await waitBackendReady(30000);
    if (!ready) {
      rxRunningRef.current = false;
      setStatus("Disconnected");
      alert("Server waking. Try again.");
      return;
    }

    setStatus("Creating room...");
    let code = "";
    try {
      const response = await fetch(`${BACKEND}/api/session/create`, { 
        method: "POST" 
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      
      const data = await response.json();
      code = data.room_code;
      setRxCode(code);
    } catch (error) {
      rxRunningRef.current = false;
      setStatus("Disconnected");
      alert("Could not create room. Try again.");
      return;
    }

    setStatus("Waiting for sender...");
    const ws = new WebSocket(`${WS_BASE}/api/ws/${rxIdRef.current}`);
    rxWsRef.current = ws;

    ws.onopen = () => {
      ws.send(JSON.stringify({ 
        type: "join", 
        room_code: code, 
        role: "receiver" 
      }));
    };

    ws.onmessage = async (event) => {
      const message = JSON.parse(event.data);
      
      if (message.type === "sender_joined") {
        setConnectedCount(count => count + 1);
      }
      
      if (message.type === "sender_left") {
        setConnectedCount(count => Math.max(0, count - 1));
      }
      
      if (message.type === "offer") {
        await handleReceiverOffer(message);
      }
      
      if (message.type === "candidate" && rxPcRef.current) {
        try {
          await rxPcRef.current.addIceCandidate(message.candidate);
        } catch (error) {
          // Ignore candidate errors
        }
      }
      
      if (["sharing_stopped", "bye", "sender_left"].includes(message.type)) {
        exitFullscreenNow();
        clearReceiverVideo();
        setStatus("Waiting for sender...");
      }
    };

    ws.onclose = () => {
      setStatus("Disconnected");
      rxRunningRef.current = false;
      setConnectedCount(0);
      setRxHasStream(false);
    };

    ws.onerror = () => setStatus("Receiver WS error");
  }

  async function handleReceiverOffer(message) {
    setStatus("Connecting...");
    const pc = new RTCPeerConnection(RTC_CONFIG);
    rxPcRef.current = pc;

    pc.onicecandidate = (event) => {
      if (event.candidate && rxWsRef.current) {
        rxWsRef.current.send(JSON.stringify({
          type: "candidate",
          candidate: event.candidate,
          sender_id: message.sender_id
        }));
      }
    };

    pc.ontrack = (event) => {
      const video = rxVideoRef.current;
      if (video && event.streams && event.streams[0]) {
        video.srcObject = event.streams[0];
        video.muted = rxMuted;
        video.volume = 1.0;
        setRxHasStream(true);
        const playPromise = video.play();
        if (playPromise && playPromise.catch) {
          playPromise.catch(() => {});
        }
      }
    };

    await pc.setRemoteDescription(message.offer);
    const answer = await pc.createAnswer();
    await pc.setLocalDescription(answer);
    
    rxWsRef.current?.send(JSON.stringify({
      type: "answer",
      answer,
      sender_id: message.sender_id
    }));
    setStatus("Connected");
  }

  function clearReceiverVideo() {
    try {
      const video = rxVideoRef.current;
      if (video?.srcObject) {
        const tracks = video.srcObject.getTracks?.() || [];
        tracks.forEach(track => track.stop?.());
        video.srcObject = null;
      }
    } catch (error) {
      // Ignore cleanup errors
    }
    setRxHasStream(false);
  }

  // Sender functions
  async function startSender() {
    const code = (txCode || "").trim().toUpperCase();
    if (!code || code.length < 4) {
      alert("Enter 6-digit code");
      return;
    }

    setStatus("Waking backend...");
    const ready = await waitBackendReady(30000);
    if (!ready) {
      setStatus("Disconnected");
      alert("Server waking. Try again.");
      return;
    }

    try {
      const response = await fetch(`${BACKEND}/api/session/${code}`);
      const data = await response.json();
      if (!data.exists) {
        alert("Room does not exist");
        setStatus("Disconnected");
        return;
      }
    } catch (error) {
      alert("Failed to reach backend");
      setStatus("Disconnected");
      return;
    }

    setStatus("Requesting screen...");
    let stream;
    try {
      stream = await navigator.mediaDevices.getDisplayMedia({
        video: true,
        audio: includeAudio
      });
    } catch (error) {
      alert("Screen capture blocked or unsupported");
      setStatus("Disconnected");
      return;
    }

    txStreamRef.current = stream;
    if (txVideoRef.current) {
      txVideoRef.current.srcObject = stream;
      txVideoRef.current.muted = true;
      const playPromise = txVideoRef.current.play();
      if (playPromise && playPromise.catch) {
        playPromise.catch(() => {});
      }
    }

    setStatus("Connecting...");
    const pc = new RTCPeerConnection(RTC_CONFIG);
    txPcRef.current = pc;
    stream.getTracks().forEach(track => pc.addTrack(track, stream));

    const ws = new WebSocket(`${WS_BASE}/api/ws/${txIdRef.current}`);
    txWsRef.current = ws;

    pc.onicecandidate = (event) => {
      if (event.candidate && ws.readyState === 1) {
        ws.send(JSON.stringify({
          type: "candidate",
          candidate: event.candidate
        }));
      }
    };

    ws.onopen = async () => {
      ws.send(JSON.stringify({
        type: "join",
        room_code: code,
        role: "sender"
      }));
      
      const offer = await pc.createOffer({
        offerToReceiveAudio: true,
        offerToReceiveVideo: true
      });
      
      await pc.setLocalDescription(offer);
      ws.send(JSON.stringify({ type: "offer", offer }));
      setStatus("Offer sent");
    };

    ws.onmessage = async (event) => {
      const message = JSON.parse(event.data);
      
      if (message.type === "answer") {
        await pc.setRemoteDescription(message.answer);
        setStatus("Connected");
        setSenderConnected(true);
      }
      
      if (message.type === "candidate") {
        try {
          await pc.addIceCandidate(message.candidate);
        } catch (error) {
          // Ignore candidate errors
        }
      }
      
      if (message.type === "receiver_left" || message.type === "bye") {
        stopSender();
        setStatus("Receiver left");
      }
    };

    ws.onerror = () => setStatus("Sender WS error");
    ws.onclose = () => setStatus("Sender disconnected");

    // Stop when user ends screen share
    const videoTrack = stream.getVideoTracks()[0];
    if (videoTrack) {
      videoTrack.addEventListener("ended", () => stopSender());
    }
  }

  function stopSender() {
    try {
      txWsRef.current?.send(JSON.stringify({ type: "sharing_stopped" }));
      txWsRef.current?.send(JSON.stringify({ type: "bye" }));
    } catch (error) {
      // Ignore send errors
    }

    try {
      txWsRef.current?.close();
    } catch (error) {
      // Ignore close errors
    }
    txWsRef.current = null;

    try {
      txPcRef.current?.getSenders().forEach(sender => {
        if (sender.track) sender.track.stop();
      });
    } catch (error) {
      // Ignore cleanup errors
    }

    try {
      txPcRef.current?.close();
    } catch (error) {
      // Ignore close errors
    }
    txPcRef.current = null;

    try {
      txStreamRef.current?.getTracks().forEach(track => track.stop());
    } catch (error) {
      // Ignore cleanup errors
    }
    txStreamRef.current = null;

    setSenderConnected(false);
    setStatus("Disconnected");
  }

  function disconnectSender() {
    stopSender();
    setTxCode("");
  }

  // UI Components
  const CodeDots = ({ code }) => {
    const chars = (code || "").padEnd(6, " ").slice(0, 6).split("");
    return (
      <div className="code-dots">
        {chars.map((char, index) => (
          <div className="dot" key={index}>
            {/^[A-Z0-9]$/.test(char) ? char : " "}
          </div>
        ))}
      </div>
    );
  };

  const TopBar = (
    <div className="topbar">
      {page !== "home" ? (
        <button 
          className="link-back" 
          onClick={() => {
            setPage("home");
            setStatus("Disconnected");
          }}
        >
          ← Back to Home
        </button>
      ) : (
        <span />
      )}
      <div className="status-right">
        <span className="status-dot" />
        {status}
      </div>
    </div>
  );

  return (
    <div className="ui-wrap">
      {TopBar}

      {page === "home" && (
        <div className="landing">
          <h1 className="brand-hero">
            <IconCast />
            OneTapCast
          </h1>
          <p className="tagline">
            Share your screen seamlessly across all devices. Cast from any browser to any browser — no apps required.
          </p>

          <div className="cards-2">
            <div 
              className="card-cta" 
              onClick={() => setPage("receiver")} 
              role="button" 
              tabIndex={0}
            >
              <div className="cta-icon">
                <IconArrowDownCircle />
              </div>
              <div className="cta-title">View Screen</div>
              <ul className="cta-list">
                <li>Get a pairing code</li>
                <li>View shared screens</li>
              </ul>
            </div>

            <div 
              className="card-cta" 
              onClick={() => setPage("sender")} 
              role="button" 
              tabIndex={0}
            >
              <div className="cta-icon green">
                <IconArrowUpCircle />
              </div>
              <div className="cta-title">Share Screen</div>
              <ul className="cta-list">
                <li>Enter pairing code</li>
                <li>Start screen sharing</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {page === "sender" && (
        <div className="sender-wrap">
          <div className="card wide">
            <div className="card-title">
              <IconArrowUpCircle />
              Sharing Mode
            </div>
            <div className="card-sub">Enter the room code from the receiving device</div>
            <div className="sender-row">
              <input
                className="input large"
                placeholder="Enter 6-digit code"
                value={txCode}
                onChange={e => setTxCode(e.target.value.toUpperCase())}
              />
              {!senderConnected ? (
                <Button className="secondary" onClick={startSender}>
                  Connect
                </Button>
              ) : (
                <>
                  <Button className="secondary" onClick={stopSender}>
                    Stop Sharing
                  </Button>
                  <Button className="ghost" onClick={disconnectSender}>
                    Disconnect
                  </Button>
                </>
              )}
            </div>
            <div className="hint">
              Tip: To share audio on desktop, share a Chrome tab and check "Share tab audio". OS-level audio for entire screen is restricted.
            </div>
            <div className="sender-controls">
              <label className="inline">
                <input
                  type="checkbox"
                  checked={includeAudio}
                  onChange={e => setIncludeAudio(e.target.checked)}
                />
                Include Audio
              </label>
            </div>
          </div>
          <video 
            ref={txVideoRef} 
            autoPlay 
            playsInline 
            muted 
            style={{ display: "none" }} 
          />
        </div>
      )}

      {page === "receiver" && (
        <div className="receiver-grid">
          <div className="left">
            <div className="card">
              <div className="card-title">
                <IconArrowDownCircle />
                Viewing Mode
              </div>
              <div className="card-sub">Share this code with devices you want to cast from</div>
              <CodeDots code={rxCode} />
              <div className="row">
                <Button
                  className="ghost"
                  onClick={() => copyToClipboard(rxCode)}
                  disabled={!rxCode}
                >
                  Copy Code
                </Button>
                <Button
                  className="ghost"
                  onClick={() => setRxFit(rxFit === "contain" ? "cover" : "contain")}
                >
                  Fit: {rxFit === "contain" ? "Contain" : "Cover"}
                </Button>
                <label className="inline">
                  <input
                    type="checkbox"
                    checked={rxMuted}
                    onChange={e => {
                      setRxMuted(e.target.checked);
                      try {
                        const video = rxVideoRef.current;
                        if (video) {
                          video.muted = e.target.checked;
                          if (!e.target.checked) {
                            video.play().catch(() => {});
                          }
                        }
                      } catch (error) {
                        // Ignore video control errors
                      }
                    }}
                  />
                  Mute
                </label>
              </div>
            </div>

            <div className="card">
              <div className="card-title">Connected Devices ({connectedCount})</div>
              <div className="muted">
                {connectedCount === 0 ? "No devices connected yet" : "Devices connected"}
              </div>
            </div>
          </div>

          <div className="right">
            <div 
              id="rx-pane" 
              className={`screen-wrap ${rxFit === "cover" ? "fit-cover" : "fit-contain"}`}
            >
              <video
                ref={rxVideoRef}
                autoPlay
                playsInline
                controls={false}
                muted={rxMuted}
                className="screen-video"
              />
              {!rxHasStream && (
                <div className="screen-overlay">
                  <div className="screen-placeholder">
                    <IconMonitor />
                    <div className="ph-title">Waiting for screen share</div>
                    <div className="ph-sub">Share the room code to start receiving</div>
                  </div>
                </div>
              )}
              {rxHasStream && rxMuted && (
                <div 
                  className="unmute-overlay" 
                  onClick={() => {
                    setRxMuted(false);
                    try {
                      const video = rxVideoRef.current;
                      if (video) {
                        video.muted = false;
                        video.volume = 1.0;
                        video.play().catch(() => {});
                      }
                    } catch (error) {
                      // Ignore unmute errors
                    }
                  }}
                >
                  Tap to unmute
                </div>
              )}
            </div>
            <div className="right-actions">
              <Button
                className="ghost"
                onClick={() => enterFullscreenOrImmersive("rx-pane")}
              >
                Fullscreen
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
